// =====================================================================
// Live chat FAQ list (2026-10-25, Round 1). Answers the chat gives
// instantly, with no AI. Each FAQ has trigger phrases (matched against the
// visitor's message), the answer text and the buttons shown under it.
//
// Tokens filled in from the live paper list when the answer is shown:
//   {paperCount}     number of papers with practice questions
//   {liveFamilies}   exam families that have practice content today
//   {soonFamilies}   exam families that are coming soon
//
// Buttons are chosen per visitor in FloatingSupport.tsx: a guest sees
// "Try 5 free questions" and "Create free account", a free member sees
// "See plans", a subscriber never sees a sales button.
// Round 3 moves this list into the admin dashboard so it can be edited.
// =====================================================================

export type ChatActionKey =
  | 'tryFree'
  | 'signup'
  | 'login'
  | 'pricing'
  | 'exams'
  | 'mocks'
  | 'guides'
  | 'flashcards'
  | 'review'
  | 'tutoring'
  | 'dashboard'
  | 'whatsapp'
  | 'handoff';

export interface ChatFaq {
  id: string;
  category: 'pricing' | 'free' | 'account' | 'product' | 'exam' | 'support';
  question: string;
  keywords: string[];
  answer: string;
  actions: ChatActionKey[];
}

export const CHAT_FAQS: ChatFaq[] = [
  // ---------------- Pricing and plans ----------------
  {
    id: 'pricing',
    category: 'pricing',
    question: 'How much does PrePraxis cost?',
    keywords: ['price', 'pricing', 'cost', 'how much', 'fee', 'fees', 'plan', 'plans', 'subscription', 'subscribe', 'pay', 'monthly', 'per month', 'expensive', 'cheap', 'discount', 'all access', 'all-access'],
    answer:
      'One subscription unlocks every exam and every paper on PrePraxis:\n' +
      '• Basic: $35 for 1 month\n' +
      '• Standard: $60 for 2 months (works out at $30 a month)\n' +
      '• Extended: $112 for 4 months (works out at $28 a month)\n' +
      'Every plan includes all practice questions, full timed mock exams with score reports, study guides, flashcards and the review queue.',
    actions: ['pricing', 'tryFree', 'signup']
  },
  {
    id: 'whats-included',
    category: 'pricing',
    question: 'What do I get with a subscription?',
    keywords: ['included', 'include', 'what do i get', 'benefits', 'features', 'unlock', 'full access', 'premium', 'worth it', 'what comes with'],
    answer:
      'A subscription unlocks everything, on every paper:\n' +
      '• The full question bank, each question with a clear explanation\n' +
      '• Full length timed mock exams, two per paper for most papers, with a score report by topic\n' +
      '• Study guides and flashcards\n' +
      '• A review queue that brings back the questions you missed at the right time\n' +
      '• A study plan built around your exam date',
    actions: ['pricing', 'tryFree']
  },
  {
    id: 'which-plan',
    category: 'pricing',
    question: 'Which plan should I choose?',
    keywords: ['which plan', 'best plan', 'recommend a plan', 'what plan', 'choose a plan', 'better plan', 'basic or', 'standard or', 'extended or'],
    answer:
      'It depends on how far away your exam is:\n' +
      '• Exam in the next 4 weeks: Basic ($35, 1 month)\n' +
      '• Exam in 1 to 2 months: Standard ($60 for 2 months), our most popular plan\n' +
      '• Exam in 3 to 4 months, or more than one exam to pass: Extended ($112 for 4 months), the best value\n' +
      'All three plans unlock exactly the same content.',
    actions: ['pricing']
  },
  {
    id: 'payment-methods',
    category: 'pricing',
    question: 'How can I pay?',
    keywords: ['payment method', 'pay with', 'card', 'credit card', 'debit card', 'paystack', 'mpesa', 'm-pesa', 'paypal', 'bank', 'checkout', 'how to pay', 'how do i pay'],
    answer:
      'Payments are made securely through Paystack. Choose a plan, enter your card details on the Paystack page, and your access unlocks as soon as the payment goes through. You need a free account first, so your access is saved to you.',
    actions: ['pricing', 'signup']
  },
  {
    id: 'payment-problem',
    category: 'support',
    question: 'My payment failed or I paid but have no access',
    keywords: ['payment failed', 'declined', 'denied', 'card declined', 'paid but', 'charged', 'no access', 'not unlocked', 'still locked', 'payment problem', 'payment issue', 'double charged', 'fraud'],
    answer:
      'Sorry about that. Try these first:\n' +
      '• Refresh the page and sign in again, access sometimes takes a minute to show\n' +
      '• If the card was declined, try a different card or ask your bank to allow the payment\n' +
      'If you were charged and still have no access, message us on WhatsApp with the email you signed up with and we will sort it out quickly.',
    actions: ['whatsapp', 'handoff']
  },
  {
    id: 'cancel-refund',
    category: 'support',
    question: 'Can I cancel or get a refund?',
    keywords: ['cancel', 'cancellation', 'refund', 'money back', 'stop subscription', 'unsubscribe', 'renew', 'renewal', 'auto renew'],
    answer:
      'Your access lasts for the full period you paid for. For anything about cancelling, renewing or refunds, message us on WhatsApp with the email on your account and we will help you personally.',
    actions: ['whatsapp', 'handoff']
  },

  // ---------------- Free questions ----------------
  {
    id: 'free-questions',
    category: 'free',
    question: 'Can I try it for free?',
    keywords: ['free', 'for free', 'trial', 'free trial', 'try', 'preview', 'demo', 'sample', 'sample questions', 'no card', 'without paying'],
    answer:
      'Yes. You can answer 5 practice questions right now with no sign up and no card. Create a free account and you get 5 free questions on every paper, plus 5 more on the paper you started with. Each question comes with a full explanation.',
    actions: ['tryFree', 'signup']
  },
  {
    id: 'free-used',
    category: 'free',
    question: 'I used my free questions. What now?',
    keywords: ['used my free', 'no more free', 'free questions used', 'ran out', 'limit reached', 'locked after 5', 'only 5'],
    answer:
      'Nice work getting through them. If you are a guest, create a free account to get 5 more on the same paper and 5 on every other paper. To practise without limits and sit the full mock exams, choose a plan from $28 a month.',
    actions: ['signup', 'pricing']
  },

  // ---------------- Exams covered ----------------
  {
    id: 'exams-covered',
    category: 'exam',
    question: 'Which exams do you cover?',
    keywords: ['which exams', 'what exams', 'exams covered', 'coverage', 'do you cover', 'exams do you have', 'supported', 'available exams', 'list of exams', 'states', 'which states', 'my state'],
    answer:
      'We have practice content for {paperCount} papers today, across {liveFamilies}. Coming soon: {soonFamilies}. Tell me your exam code or subject (for example "5733" or "biology") and I will show you what we have for it.',
    actions: ['exams', 'tryFree']
  },
  {
    id: 'praxis-what',
    category: 'exam',
    question: 'What is the Praxis?',
    keywords: ['what is praxis', 'what is the praxis', 'praxis exam', 'praxis test', 'ets', 'what is core', 'praxis core'],
    answer:
      'The Praxis tests are teacher certification exams run by ETS and used by many US states. Most candidates take the Praxis Core (Reading 5713, Writing 5723, Math 5733) and one or more Subject tests for the grade or subject they will teach. PrePraxis is an independent practice platform and is not affiliated with ETS.',
    actions: ['exams', 'tryFree']
  },
  {
    id: 'which-test',
    category: 'exam',
    question: 'Which test do I need to take?',
    keywords: ['which test do i need', 'what test do i need', 'which exam do i need', 'required test', 'requirements', 'certification requirements', 'license requirements', 'what do i need to take'],
    answer:
      'Each state decides which tests you need, so check your state education department or your teacher preparation program first. Once you know your test codes, tell me one and I will show you how we can help you prepare. If you are unsure, book a free consultation and we will help you work it out.',
    actions: ['tutoring', 'exams']
  },
  {
    id: 'passing-score',
    category: 'exam',
    question: 'What score do I need to pass?',
    keywords: ['passing score', 'pass score', 'score to pass', 'cut score', 'qualifying score', 'what score', 'minimum score', 'pass mark'],
    answer:
      'Passing scores are set by each state, not by ETS, and they differ from state to state. Check the score your state requires for your test. Our mock exams give you a score report by topic so you can see how close you are and what to work on.',
    actions: ['mocks', 'tryFree']
  },
  {
    id: 'retake',
    category: 'exam',
    question: 'How soon can I retake the exam?',
    keywords: ['retake', 'retest', 'failed the exam', 'failed my exam', 'take it again', 'second attempt', 'how many times'],
    answer:
      'For Praxis tests, ETS asks you to wait 28 days before you retake the same test. Use that time well: take a mock exam, see which topics cost you points, and practise those first. Check the ETS website for the latest rules for your test.',
    actions: ['mocks', 'tutoring']
  },
  {
    id: 'register-exam',
    category: 'exam',
    question: 'How do I register for my exam?',
    keywords: ['register for', 'book the exam', 'book my exam', 'schedule my exam', 'test date', 'test center', 'testing center', 'sign up for the exam', 'exam registration'],
    answer:
      'You register for Praxis tests on the ETS website (for California tests, on the CTC testing site). We do not handle exam registration. Once you have a date, add it to your study plan here and we will help you get ready for it.',
    actions: ['dashboard', 'signup']
  },
  {
    id: 'how-long-study',
    category: 'exam',
    question: 'How long should I study?',
    keywords: ['how long', 'how many weeks', 'how much time', 'study time', 'study plan', 'schedule', 'prepare in', 'enough time', 'start studying'],
    answer:
      'Many candidates prepare for 4 to 8 weeks with 30 to 60 minutes a day, but it depends on your starting point. Start with a short set of questions to see where you stand. When you sign up, we build a week by week plan around your exam date and daily goal.',
    actions: ['signup', 'tryFree']
  },

  // ---------------- Product features ----------------
  {
    id: 'mock-exams',
    category: 'product',
    question: 'Do you have full mock exams?',
    keywords: ['mock', 'mocks', 'mock exam', 'mock test', 'practice test', 'full test', 'full length', 'timed test', 'simulation', 'real exam'],
    answer:
      'Yes. Most papers have two full length mock exams that match the real test: the same number of questions, the same time limit and the same mix of topics. The two mocks never share a question. After each one you get a score report by topic. Mock exams are part of every plan.',
    actions: ['mocks', 'pricing']
  },
  {
    id: 'questions-quality',
    category: 'product',
    question: 'Are the questions like the real exam?',
    keywords: ['real exam questions', 'like the real', 'similar to the real', 'accurate', 'quality', 'official', 'actual questions', 'harder', 'easier', 'realistic'],
    answer:
      'Our questions are written from the official test blueprints, so they cover the same topics in the same proportions and at a similar difficulty. They are original practice questions, not copies of real exam items, and each one has an explanation of why the answer is right.',
    actions: ['tryFree']
  },
  {
    id: 'study-guides',
    category: 'product',
    question: 'Do you have study guides?',
    keywords: ['study guide', 'study guides', 'guide', 'notes', 'pdf', 'review sheet', 'summary', 'textbook', 'book'],
    answer:
      'Yes, study guides are available for a growing list of papers and are included in every plan. Open your exam to see whether its guide is ready. Each guide covers the topics, worked examples, common mistakes and practice questions.',
    actions: ['guides', 'pricing']
  },
  {
    id: 'flashcards',
    category: 'product',
    question: 'Do you have flashcards?',
    keywords: ['flashcard', 'flashcards', 'flash cards', 'memorize', 'memorise', 'vocabulary', 'terms'],
    answer:
      'Yes. Each paper has a flashcard deck, and when you miss a question we add a personal flashcard for that idea. Cards come back on a schedule so the hard ones stay fresh.',
    actions: ['flashcards', 'pricing']
  },
  {
    id: 'review-queue',
    category: 'product',
    question: 'What is the review queue?',
    keywords: ['review queue', 'spaced repetition', 'review', 'missed questions', 'wrong answers', 'mistakes'],
    answer:
      'The review queue brings back questions you got wrong, a day later, then a few days later, then a week later, until you get them right every time. It is one of the fastest ways to turn weak topics into points.',
    actions: ['review', 'pricing']
  },
  {
    id: 'progress',
    category: 'product',
    question: 'Can I track my progress?',
    keywords: ['progress', 'track', 'tracking', 'dashboard', 'score report', 'results', 'how am i doing', 'weak areas', 'strengths'],
    answer:
      'Yes. Your dashboard shows your accuracy by topic, your mock exam scores, your study streak and the days left to your exam, so you always know what to work on next.',
    actions: ['dashboard', 'signup']
  },
  {
    id: 'essay',
    category: 'product',
    question: 'Can I practise the essays?',
    keywords: ['essay', 'essays', 'writing task', 'constructed response', 'written response', 'argumentative', 'informative essay', 'feedback on my writing'],
    answer:
      'Yes. Papers with written tasks, such as Core Writing 5723, include essay prompts. You can write your response in the app and get feedback against the scoring rubric.',
    actions: ['tryFree', 'pricing']
  },
  {
    id: 'tutoring',
    category: 'product',
    question: 'Do you offer tutoring?',
    keywords: ['tutor', 'tutoring', 'tutors', 'one on one', '1 on 1', '1-on-1', 'teacher help', 'coach', 'coaching', 'consultation', 'personal help', 'live class', 'lessons'],
    answer:
      'Yes. You can book 1 on 1 tutoring, and the first consultation is free: tell us your exam and goals, and we will reach out to plan the right support for you.',
    actions: ['tutoring', 'whatsapp']
  },

  // ---------------- Account ----------------
  {
    id: 'create-account',
    category: 'account',
    question: 'How do I create an account?',
    keywords: ['create account', 'create an account', 'sign up', 'signup', 'register', 'registration', 'join', 'open an account', 'new account'],
    answer:
      'Click "Create free account", sign up with Google or your email, and confirm your email with the link we send. Then pick your exam, your exam date and your daily goal, and we build your study plan. It takes about a minute.',
    actions: ['signup']
  },
  {
    id: 'verify-email',
    category: 'account',
    question: 'I did not get the verification email',
    keywords: ['verification', 'verify', 'verification email', 'confirm email', 'confirmation email', 'no email', 'did not get the email', "didn't get", 'link expired', 'spam'],
    answer:
      'Check your spam and promotions folders for an email from PrePraxis. On the "Check your inbox" screen you can press "Resend link" after a minute. If the email address was mistyped, sign out and create the account again with the right one. Signing up with Google skips this step.',
    actions: ['handoff']
  },
  {
    id: 'forgot-password',
    category: 'account',
    question: 'I forgot my password',
    keywords: ['forgot password', 'forgot my password', 'reset password', 'password reset', 'cant log in', "can't log in", 'cannot log in', 'login problem', 'log in problem', 'locked out', 'sign in problem'],
    answer:
      'Click "Log In", then "Forgot password?", and enter your email. We will send you a link to set a new password. If you signed up with Google, use "Continue with Google" instead.',
    actions: ['login']
  },
  {
    id: 'devices',
    category: 'account',
    question: 'Can I use it on my phone?',
    keywords: ['phone', 'mobile', 'tablet', 'ipad', 'iphone', 'android', 'app', 'download the app', 'laptop', 'computer', 'device', 'devices'],
    answer:
      'Yes. PrePraxis works in any modern browser on your phone, tablet or computer, with nothing to download. Sign in with the same account and your progress follows you.',
    actions: ['tryFree']
  },
  {
    id: 'change-exam',
    category: 'account',
    question: 'How do I change my exam or exam date?',
    keywords: ['change my exam', 'change exam', 'change goal', 'change my goal', 'change date', 'exam date', 'switch exam', 'different exam', 'second exam', 'another exam'],
    answer:
      'Open your account (top right) and use "Change" next to your goal exam. A subscription covers every exam, so you can study for more than one at no extra cost.',
    actions: ['dashboard']
  },

  // ---------------- Support ----------------
  {
    id: 'contact',
    category: 'support',
    question: 'How can I contact you?',
    keywords: ['contact', 'support', 'help', 'email you', 'phone number', 'whatsapp', 'call', 'customer service', 'reach you'],
    answer:
      'You can message us on WhatsApp at +1 661 531 0357, or leave a message here with your email and we will get back to you.',
    actions: ['whatsapp', 'handoff']
  },
  {
    id: 'technical',
    category: 'support',
    question: 'Something is not working',
    keywords: ['not working', 'bug', 'error', 'broken', 'stuck', 'loading', 'blank page', 'crash', 'glitch', 'problem', 'issue'],
    answer:
      'Sorry about that. A hard refresh fixes most problems (Ctrl+Shift+R on Windows, Cmd+Shift+R on Mac), then sign in again. If it still happens, leave us a message with what you were doing and we will look into it.',
    actions: ['handoff', 'whatsapp']
  },
  {
    id: 'schools',
    category: 'support',
    question: 'Do you offer plans for schools or groups?',
    keywords: ['school', 'schools', 'district', 'university', 'college', 'program', 'group', 'cohort', 'bulk', 'institution', 'licence', 'license for'],
    answer:
      'Yes, we can set up access for teacher preparation programs, schools and study groups. Leave us a message or contact us on WhatsApp with how many candidates and which exams, and we will get back to you with options.',
    actions: ['handoff', 'whatsapp']
  }
];

// Words that mean "I want a real person".
export const HANDOFF_PHRASES = [
  'human', 'real person', 'a person', 'agent', 'talk to someone', 'speak to someone', 'talk to a', 'speak to a',
  'customer care', 'representative', 'someone help', 'call me', 'live agent'
];

// Words that, next to a paper, mean the visitor wants that paper's details.
export const PAPER_DETAIL_PHRASES = [
  'what is on', "what's on", 'whats on', 'topics', 'content', 'cover', 'covers', 'format', 'how many questions',
  'how long is', 'time limit', 'minutes', 'domains', 'sections', 'blueprint', 'about', 'tell me', 'info', 'details'
];
